import { useCallback, useEffect, useRef, useState } from "react"

import * as fabric from "fabric"
import { useTranslation } from "react-i18next"

import type { EditorTool } from "./editor-types"

import { Spinner } from "@/components/ui/spinner"

interface EditorCanvasProps {
  imageUrl: string
  activeTool: EditorTool
  brushSize: number
  maskOpacity: number
  onBeforeMaskChange?: () => void
  onMaskChange?: () => void
  canvasRef: React.MutableRefObject<fabric.Canvas | null>
  pendingRestore?: string | null
  onRestoreComplete?: () => void
  onImageLoaded?: (width: number, height: number) => void
}

export function EditorCanvas({
  imageUrl,
  activeTool,
  brushSize,
  maskOpacity,
  onBeforeMaskChange,
  onMaskChange,
  canvasRef,
  pendingRestore,
  onRestoreComplete,
  onImageLoaded,
}: EditorCanvasProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.imageEditor" })
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasElRef = useRef<HTMLCanvasElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [, setDimensions] = useState({ width: 0, height: 0 })
  const isDrawingShape = useRef(false)
  const shapeStart = useRef({ x: 0, y: 0 })
  const activeShape = useRef<fabric.Rect | fabric.Ellipse | null>(null)
  // Combined init + resize: canvas is sized to the IMAGE (not the container),
  // so the image always fills 100% of the canvas with no empty space.
  // A ResizeObserver handles both initial layout and subsequent resizes.
  useEffect(() => {
    if (!canvasElRef.current || !containerRef.current) return

    setIsLoaded(false)

    const container = containerRef.current
    const canvasEl = canvasElRef.current
    let canvas: fabric.Canvas | null = null
    let disposed = false
    let loadedImg: HTMLImageElement | null = null
    let rafId: number | null = null

    // Create canvas sized to the image's display dimensions (not the container)
    const initCanvas = (availW: number, availH: number) => {
      if (disposed || canvas || !loadedImg) return

      const imgW = loadedImg.naturalWidth || loadedImg.width
      const imgH = loadedImg.naturalHeight || loadedImg.height
      if (imgW === 0 || imgH === 0) return

      const scale = Math.min(availW / imgW, availH / imgH)
      const canvasW = Math.floor(imgW * scale)
      const canvasH = Math.floor(imgH * scale)

      canvas = new fabric.Canvas(canvasEl, {
        width: canvasW,
        height: canvasH,
        isDrawingMode: false,
        selection: false,
        backgroundColor: "transparent",
      })

      canvasRef.current = canvas
      setDimensions({ width: canvasW, height: canvasH })

      const fabricImg = new fabric.FabricImage(loadedImg, {
        left: 0,
        top: 0,
        originX: "left",
        originY: "top",
        selectable: false,
        evented: false,
      })
      canvas.add(fabricImg)
      const scaleFactor = canvasW / fabricImg.width
      fabricImg.set({ scaleX: scaleFactor, scaleY: scaleFactor })
      fabricImg.setCoords()
      canvas.sendObjectToBack(fabricImg)
      canvas.renderAll()
      setIsLoaded(true)
    }

    // Resize canvas to match image at new available dimensions
    const resizeCanvas = (availW: number, availH: number) => {
      if (!canvas || !loadedImg) return

      const imgW = loadedImg.naturalWidth || loadedImg.width
      const imgH = loadedImg.naturalHeight || loadedImg.height
      if (imgW === 0 || imgH === 0) return

      const prevW = canvas.getWidth() || 1
      const prevH = canvas.getHeight() || 1
      const scale = Math.min(availW / imgW, availH / imgH)
      const canvasW = Math.floor(imgW * scale)
      const canvasH = Math.floor(imgH * scale)
      const ratioX = canvasW / prevW
      const ratioY = canvasH / prevH

      const objects = canvas.getObjects()

      canvas.setDimensions({ width: canvasW, height: canvasH })

      const bgImage = objects.find((obj) => obj instanceof fabric.FabricImage) as
        | fabric.FabricImage
        | undefined
      if (bgImage) {
        const sf = canvasW / bgImage.width
        bgImage.set({ left: 0, top: 0, scaleX: sf, scaleY: sf })
        bgImage.setCoords()
      }

      for (const obj of objects) {
        if (obj instanceof fabric.FabricImage) continue
        obj.set({
          left: obj.left * ratioX,
          top: obj.top * ratioY,
          scaleX: obj.scaleX * ratioX,
          scaleY: obj.scaleY * ratioY,
        })
        if ("strokeWidth" in obj && typeof obj.strokeWidth === "number") {
          obj.set("strokeWidth", obj.strokeWidth * ((ratioX + ratioY) / 2))
        }
        obj.setCoords()
      }

      canvas.renderAll()
      setDimensions({ width: canvasW, height: canvasH })
    }

    // Load the image first
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      if (disposed) return
      loadedImg = img
      onImageLoaded?.(img.naturalWidth, img.naturalHeight)
      const w = container.clientWidth
      const h = container.clientHeight
      if (w > 0 && h > 0) initCanvas(w, h)
    }
    img.src = imageUrl

    // Observe container — handles late layout and resizes
    const observer = new ResizeObserver(() => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (disposed) return
        const w = container.clientWidth
        const h = container.clientHeight
        if (w === 0 || h === 0) return

        if (!canvas) {
          initCanvas(w, h)
        } else {
          resizeCanvas(w, h)
        }
      })
    })
    observer.observe(container)

    return () => {
      disposed = true
      observer.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (canvas) void canvas.dispose()
      canvasRef.current = null
    }
  }, [imageUrl])

  // Restore canvas from pending undo/redo state
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !pendingRestore) return

    void canvas.loadFromJSON(pendingRestore).then(() => {
      canvas.renderAll()
      onRestoreComplete?.()
    })
  }, [pendingRestore, onRestoreComplete])

  // Update tool mode
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Reset drawing mode
    canvas.isDrawingMode = false
    canvas.selection = false
    canvas.defaultCursor = "crosshair"

    if (activeTool === "brush") {
      canvas.isDrawingMode = true
      const brush = new fabric.PencilBrush(canvas)
      brush.color = `rgba(255, 0, 0, ${maskOpacity})`
      brush.width = brushSize
      canvas.freeDrawingBrush = brush
    }

    // Lock image objects from being selected
    canvas.getObjects().forEach((obj) => {
      if (obj instanceof fabric.FabricImage) {
        obj.set({ selectable: false, evented: false })
      }
    })

    canvas.renderAll()
  }, [activeTool, brushSize, maskOpacity, isLoaded])

  // Handle shape drawing (rectangle/ellipse)
  const handleMouseDown = useCallback(
    (e: fabric.TPointerEventInfo<fabric.TPointerEvent>) => {
      const canvas = canvasRef.current
      if (!canvas || (activeTool !== "rectangle" && activeTool !== "ellipse")) return

      onBeforeMaskChange?.()

      const pointer = canvas.getViewportPoint(e.e)
      isDrawingShape.current = true
      shapeStart.current = { x: pointer.x, y: pointer.y }

      if (activeTool === "rectangle") {
        const rect = new fabric.Rect({
          left: pointer.x,
          top: pointer.y,
          width: 0,
          height: 0,
          fill: `rgba(255, 0, 0, ${maskOpacity})`,
          selectable: false,
          evented: false,
        })
        canvas.add(rect)
        activeShape.current = rect
      } else {
        const ellipse = new fabric.Ellipse({
          left: pointer.x,
          top: pointer.y,
          rx: 0,
          ry: 0,
          fill: `rgba(255, 0, 0, ${maskOpacity})`,
          selectable: false,
          evented: false,
        })
        canvas.add(ellipse)
        activeShape.current = ellipse
      }
    },
    [activeTool, maskOpacity, onBeforeMaskChange]
  )

  const handleMouseMove = useCallback((e: fabric.TPointerEventInfo<fabric.TPointerEvent>) => {
    const canvas = canvasRef.current
    if (!canvas || !isDrawingShape.current || !activeShape.current) return

    const pointer = canvas.getViewportPoint(e.e)
    const sx = shapeStart.current.x
    const sy = shapeStart.current.y
    const dx = pointer.x - sx
    const dy = pointer.y - sy

    if (activeShape.current instanceof fabric.Rect) {
      activeShape.current.set({
        left: dx > 0 ? sx : pointer.x,
        top: dy > 0 ? sy : pointer.y,
        width: Math.abs(dx),
        height: Math.abs(dy),
      })
    } else if (activeShape.current instanceof fabric.Ellipse) {
      activeShape.current.set({
        left: dx > 0 ? sx : pointer.x,
        top: dy > 0 ? sy : pointer.y,
        rx: Math.abs(dx) / 2,
        ry: Math.abs(dy) / 2,
      })
    }

    canvas.renderAll()
  }, [])

  const handleMouseUp = useCallback(() => {
    isDrawingShape.current = false
    activeShape.current = null
    onMaskChange?.()
  }, [onMaskChange])

  // Bind canvas events for shape drawing
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Capture state before drawing starts (brush/eraser) so undo works correctly
    const onBeforeDraw = () => {
      if (canvas.isDrawingMode) {
        onBeforeMaskChange?.()
      }
    }

    canvas.on("mouse:down", onBeforeDraw)
    canvas.on("mouse:down", handleMouseDown)
    canvas.on("mouse:move", handleMouseMove)
    canvas.on("mouse:up", handleMouseUp)

    // Track path:created for brush changes
    const onPathCreated = () => {
      onMaskChange?.()
      canvas.renderAll()
    }
    canvas.on("path:created", onPathCreated)

    return () => {
      canvas.off("mouse:down", onBeforeDraw)
      canvas.off("mouse:down", handleMouseDown)
      canvas.off("mouse:move", handleMouseMove)
      canvas.off("mouse:up", handleMouseUp)
      canvas.off("path:created", onPathCreated)
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp, onBeforeMaskChange, onMaskChange, isLoaded])

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
    >
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center ${isLoaded ? "hidden" : ""}`}
      >
        <Spinner className="text-primary size-8" />
        <span className="text-muted-foreground ml-2 text-sm">{t("loadingImage")}</span>
      </div>
      <canvas ref={canvasElRef} />
    </div>
  )
}
